import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, Observable } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import { ICartItem, ICartUpdate, ICatalog, IProductAvailabilityReq } from '../models/catalogs.models';
import { ILocation } from '../../auth/models/auth-response';
import { PageParams } from 'src/app/shared/models/request';
import { IInventoryFilterRequest, IProduct, IVariant } from 'src/app/shared/models/products.models';
import { HandlingUnitsHelper } from 'src/app/shared/utils/handling-units.helper';

@Injectable({
  providedIn: 'root',
})
export class CatalogsService {
  constructor(private http: HttpClient) {}

  public listAllCatalogs(page?: PageParams, key?: string): Observable<APIResponse<ICatalog>> {
    const params = new HttpParams()
      .set('page', page?.number || 0)
      .set('limit', page?.size || 0)
      .set('key', key || '')
      ;

    return this.http.get<APIResponse<ICatalog>>(environment.BASE_URL_API + 'retailers/pricelists', {
      params,
    });
  }

  public listCatalogById(
id: number, page?: PageParams, key?: string, filter?: IInventoryFilterRequest, sort_by?: string, sort_mode?: string   ): Observable<APIResponse<ICatalog>> {
    let params = new HttpParams()
      .set('page', page?.number || 0)
      .set('limit', page?.size || 0)
      .set('key', key || '')
      .set('filters', JSON.stringify(filter) || '');

    if (sort_by) {
      params = params.set('sort_by', sort_by);
    }
    if (sort_mode) {
      params = params.set('sort_mode', sort_mode);
    }

    return this.http.get<APIResponse<ICatalog>>(
      environment.BASE_URL_API + 'retailers/pricelists/' + id,
      { params }
    );
  }

  public loadCart(): Observable<APIResponse<ICartItem>> {
    return this.http.get<APIResponse<ICartItem>>(environment.BASE_URL_API + 'retailers/carts');
  }

  public addToCart(
    catalog: Partial<ICatalog>,
    product: IProduct | IVariant
  ): Observable<APIResponse<ICartItem>> {
    // qte_selected est toujours en unités totales (total units)
    const payload: any = {
      pricelist_id: catalog.id,
      product_id: 'values' in product ? product.id : product.product_id,
      product_uom_qty: product.qte_selected || 0,
      discount: product.discount,
      final_price: product.final_price,
    };

    if (product.selected_handling_unit_id) {
      payload.handling_unit_id = product.selected_handling_unit_id;
    }

    return this.http.post<APIResponse<ICartItem>>(
      environment.BASE_URL_API + 'retailers/carts/add_to_cart',
      payload
    );
  }

  public batchUpdateCart(
    catalog: Partial<ICatalog>,
    products: (IProduct | IVariant)[]
  ): Observable<APIResponse<ICartItem>> {
    const productsToUpdate = products.filter(p => p.qte_selected !== undefined && p.qte_selected >= 0);
    
    if (productsToUpdate.length === 0) {
      return this.http.get<APIResponse<ICartItem>>(environment.BASE_URL_API + 'retailers/carts');
    }

    const batchPayload = productsToUpdate.map(product => {
      // qte_selected est toujours en unités totales (total units)
      const payload: any = {
        pricelist_id: catalog.id,
        product_id: product.product_id!,
        product_uom_qty: product.qte_selected || 0,
        final_price: product.final_price,
      };
      
      const currentHUId = HandlingUnitsHelper.getCurrentHandlingUnitId(product, catalog);
      if (currentHUId) {
        payload.handling_unit_id = currentHUId;
      }
      
      return payload;
    });

    return this.http.post<APIResponse<ICartItem>>(
      environment.BASE_URL_API + 'retailers/carts/batch_update',
      { products: batchPayload }
    );
  }

  public updateCart(cart: ICartUpdate): Observable<APIResponse<ICartItem>> {
    return this.http.patch<APIResponse<ICartItem>>(
      environment.BASE_URL_API + 'retailers/carts/' + cart.id,
      cart
    );
  }

  public deleteCart(cartId: number): Observable<APIResponse<ICartItem>> {
    return this.http.delete<APIResponse<ICartItem>>(
      environment.BASE_URL_API + 'retailers/carts/' + cartId
    );
  }

  public getLocationsByCompanyId(id: number): Observable<APIResponse<ILocation>> {
    return this.http.get<APIResponse<ILocation>>(
      environment.BASE_URL_API + `companies/${id}/locations`
    );
  }

  public static isNew(create_date: string): boolean {
    const createDate = new Date(create_date);
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

    return createDate > oneWeekAgo;
  }

  public static isExpiring(end_date: string): boolean {
    if (!end_date) {
      return false;
    }

    const endDate = new Date(end_date);
    const oneWeekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    return endDate < oneWeekFromNow;
  }

  public static isExpired(end_date: string): boolean {
    if (!end_date) {
      return false;
    }

    const endDate = new Date(end_date);
    const currentDate = new Date(Date.now());

    return endDate < currentDate;
  }

  public checkProductAvailability(data: IProductAvailabilityReq): Observable<any> {
    return this.http.get(environment.BASE_URL_API + `retailers/quotations/${data.card_id}/check_availability?delivery_date=${data.delivery_date}`);
  }
}
